import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import {
  addBoardColumn,
  addTaskToSprint,
  boardSocketUrl,
  completeSprint,
  createSprint,
  createTask,
  deleteBoardColumn,
  deleteSprint,
  getBoard,
  getBurndown,
  getVelocity,
  listSprints,
  moveTask,
  reorderBoardColumns,
  startSprint,
  updateBoardColumn,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useTaskPriorities as usePriorities, taskPriorityCode, type PriorityIndex } from "../lib/priorities";
import type { BoardColumnRow, SprintRow, TaskRow } from "../lib/types";
import { formatDateShort } from "../lib/format";
import { PageBar } from "../components/Layout";
import { PriorityBadge } from "../components/Badge";
import { TagsEditor } from "../components/Tags";
import { Avatar } from "../components/Avatar";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { TaskDrawer } from "../components/TaskDrawer";

/** Map a priority code to a left-rail accent token (falls back to a neutral status). */
function railColor(task: TaskRow, priorityCode?: string): string {
  switch ((priorityCode ?? task.priority_code ?? "").toLowerCase()) {
    case "urgent":
      return "var(--st-blocked)";
    case "high":
      return "var(--st-progress)";
    case "normal":
      return "var(--st-open)";
    case "low":
      return "var(--st-closed)";
    default:
      return task.origin_ticket_id ? "var(--accent)" : "var(--st-closed)";
  }
}

/** A small inline calendar glyph for the due-date pill. */
function CalendarGlyph() {
  return (
    <svg className="icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </svg>
  );
}

/** Due-date pill state + label. */
function dueInfo(due?: number | null): { cls: string; label: string } | null {
  if (!due) return null;
  const now = new Date();
  const d = new Date(due * 1000);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86_400_000;
  const dueDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (dueDay < startOfToday) return { cls: "over", label: `Overdue · ${label}` };
  if (dueDay < startOfToday + dayMs) return { cls: "today", label: "Today" };
  return { cls: "", label };
}

/** The live Kanban board for one board id — columns, cards, presence, sprints,
 *  and the create/task modals. Reused as the standalone /boards/:id page AND
 *  inline inside a project's Board view tab (so no navigation is needed). */
export function BoardCanvas({ boardId }: { boardId: string }) {
  const { data, loading, error, reload } = useApi((signal) => getBoard(boardId, signal), [boardId]);
  const priorities = usePriorities();
  const [showCreate, setShowCreate] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [live, setLive] = useState(false);

  // ----- Drag-and-drop (HTML5 DnD; the per-card <select> stays as the
  // keyboard/accessible fallback). `dragId` = card currently being dragged,
  // `movingId` = card whose move request is in flight (shown translucent).
  const [dragId, setDragId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const dragMove = useMutation(moveTask);

  /** Apply a drop. `after_task_id` semantics (see work-db.ts moveTask):
   *  set → insert AFTER that sibling (fractional rank between it and the next);
   *  omitted/null → place at the TOP of the target column. */
  const handleDropTask = useCallback(
    async (taskId: string, columnId: string, afterTaskId: string | null) => {
      setDragId(null);
      setMovingId(taskId);
      await dragMove.run(taskId, {
        column_id: columnId,
        ...(afterTaskId ? { after_task_id: afterTaskId } : {}),
      });
      setMovingId(null);
      // Success or failure, re-fetch the authoritative order — on failure this
      // snaps the card back while the error surfaces in the Alert below.
      reload();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dragMove.run, reload],
  );

  const board = data?.board;
  const columns = data?.columns ?? [];

  // Optional live updates: open a WS to the board room; on any message, reload.
  // Wrapped so a missing/failing socket never blocks the board (graceful fallback).
  useEffect(() => {
    if (!board) return;
    let socket: WebSocket | null = null;
    let closed = false;
    try {
      socket = new WebSocket(boardSocketUrl(board.id));
      socket.onopen = () => {
        if (!closed) setLive(true);
      };
      socket.onmessage = () => {
        // Any board delta → re-fetch authoritative state from D1.
        reload();
      };
      socket.onclose = () => {
        if (!closed) setLive(false);
      };
      socket.onerror = () => {
        if (!closed) setLive(false);
      };
    } catch {
      setLive(false);
    }
    return () => {
      closed = true;
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
    };
    // Only re-open when the board id changes; reload is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board?.id]);

  if (loading) return <Loading label="Loading board…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!board) return <EmptyState>Board not found.</EmptyState>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{board.name}</h1>
          <div className="sub">
            {live ? (
              <span className="live-dot">
                <span className="live-pip" aria-hidden="true" /> Live
              </span>
            ) : (
              <span className="faint">Live updates unavailable — using manual refresh</span>
            )}
          </div>
        </div>
        <div className="btn-row">
          <button className="btn small" onClick={reload}>
            Refresh
          </button>
          <button className="btn small" onClick={() => setShowColumns(true)} title="Add, rename, reorder or remove this project's statuses">
            Manage statuses
          </button>
          {columns.length > 0 ? (
            <button className="btn primary" onClick={() => setShowCreate(true)}>
              + New project task
            </button>
          ) : null}
        </div>
      </div>

      {columns.length === 0 ? (
        <EmptyState>This board has no columns.</EmptyState>
      ) : (
        <>
          {dragMove.error ? <Alert kind="error">Couldn't move the card: {dragMove.error}</Alert> : null}
          <div className="board">
            {columns.map((col) => (
              <BoardColumn
                key={col.id}
                column={col}
                priorities={priorities}
                dragId={dragId}
                movingId={movingId}
                onDragState={setDragId}
                onDropTask={handleDropTask}
                onOpenTask={setOpenTaskId}
              />
            ))}
          </div>
        </>
      )}

      <SprintsSection boardId={board.id} tasks={columns.flatMap((c) => c.tasks)} />

      {showCreate && columns.length > 0 ? (
        <CreateBoardTaskModal
          projectId={board.project_id}
          boardId={board.id}
          columns={columns}
          priorities={priorities}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            reload();
          }}
        />
      ) : null}

      {openTaskId ? (
        <TaskDrawer
          taskId={openTaskId}
          onClose={() => setOpenTaskId(null)}
          onChanged={reload}
        />
      ) : null}

      {showColumns ? (
        <ManageColumnsModal
          boardId={board.id}
          columns={columns}
          onClose={() => setShowColumns(false)}
          onChanged={reload}
        />
      ) : null}
    </>
  );
}

/**
 * Manage a board's columns — i.e. this project's custom task statuses. Add,
 * rename, reorder (move up/down), and delete. Deleting a column re-homes its
 * cards to the first remaining column server-side so nothing is lost.
 */
function ManageColumnsModal({
  boardId,
  columns,
  onClose,
  onChanged,
}: {
  boardId: string;
  columns: BoardColumnRow[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [newName, setNewName] = useState("");
  const addMut = useMutation(addBoardColumn);
  const renameMut = useMutation(updateBoardColumn);
  const deleteMut = useMutation(deleteBoardColumn);
  const reorderMut = useMutation(reorderBoardColumns);
  const busy = addMut.pending || renameMut.pending || deleteMut.pending || reorderMut.pending;
  const err = addMut.error || renameMut.error || deleteMut.error || reorderMut.error;

  async function add() {
    if (!newName.trim()) return;
    const res = await addMut.run(boardId, { name: newName.trim() });
    if (res) {
      setNewName("");
      onChanged();
    }
  }
  async function rename(colId: string, name: string) {
    const res = await renameMut.run(boardId, colId, { name });
    if (res) onChanged();
  }
  async function setColor(colId: string, color: string | null) {
    const res = await renameMut.run(boardId, colId, { color });
    if (res) onChanged();
  }
  async function move(idx: number, dir: -1 | 1) {
    const next = idx + dir;
    if (next < 0 || next >= columns.length) return;
    const ids = columns.map((c) => c.id);
    [ids[idx], ids[next]] = [ids[next]!, ids[idx]!];
    const res = await reorderMut.run(boardId, ids);
    if (res) onChanged();
  }
  async function remove(col: BoardColumnRow) {
    const count = col.tasks?.length ?? 0;
    const msg =
      count > 0
        ? `Delete the "${col.name}" status? Its ${count} task${count === 1 ? "" : "s"} will move to the first remaining status.`
        : `Delete the "${col.name}" status?`;
    if (!(await confirmDialog({ message: msg, danger: true, confirmLabel: "Delete" }))) return;
    const res = await deleteMut.run(boardId, col.id);
    if (res) onChanged();
  }

  return (
    <Modal
      title="Manage statuses"
      onClose={onClose}
      footer={
        <button className="btn primary" onClick={onClose}>
          Done
        </button>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <p className="sub" style={{ marginTop: 0 }}>
        These are this project's board columns — its task statuses. Changes apply only to this project.
      </p>
      <ul className="col-manage-list">
        {columns.map((col, idx) => (
          <ColumnManageRow
            key={col.id}
            column={col}
            isFirst={idx === 0}
            isLast={idx === columns.length - 1}
            busy={busy}
            onRename={(name) => rename(col.id, name)}
            onColor={(color) => setColor(col.id, color)}
            onMoveUp={() => move(idx, -1)}
            onMoveDown={() => move(idx, 1)}
            onDelete={() => remove(col)}
          />
        ))}
      </ul>
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void add();
            }
          }}
          placeholder="New status (e.g. Blocked)"
          aria-label="New status name"
          disabled={busy}
        />
        <button className="btn small primary" type="button" style={{ flex: "0 0 auto" }} disabled={busy || !newName.trim()} onClick={add}>
          {addMut.pending ? <Spinner label="Adding…" /> : "Add status"}
        </button>
      </div>
    </Modal>
  );
}

/** One editable status row: rename (save on blur/Enter), reorder, delete. */
function ColumnManageRow({
  column,
  isFirst,
  isLast,
  busy,
  onRename,
  onColor,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  column: BoardColumnRow;
  isFirst: boolean;
  isLast: boolean;
  busy: boolean;
  onRename: (name: string) => void;
  onColor: (color: string | null) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(column.name);
  const hasColor = column.color != null;
  function commit() {
    const v = name.trim();
    if (v && v !== column.name) onRename(v);
    else setName(column.name);
  }
  return (
    <li className="col-manage-row">
      {/* Color swatch: click to set, or clear back to the default accent. */}
      <input
        type="color"
        value={column.color ?? "#2563eb"}
        disabled={busy}
        onChange={(e) => onColor(e.target.value)}
        title={hasColor ? "Status color (click to change)" : "Set a status color"}
        aria-label={`Color for ${column.name}`}
        style={{ width: 28, height: 28, flex: "0 0 auto", padding: 0, border: "none", background: "none", cursor: "pointer", opacity: hasColor ? 1 : 0.5 }}
      />
      {hasColor ? (
        <button className="btn small ghost" type="button" disabled={busy} onClick={() => onColor(null)} title="Use the default accent" aria-label="Clear color">
          ✕
        </button>
      ) : null}
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        aria-label={`Rename ${column.name}`}
        disabled={busy}
      />
      <span className="faint col-manage-count">{column.tasks?.length ?? 0}</span>
      <button className="btn small" type="button" disabled={busy || isFirst} onClick={onMoveUp} aria-label="Move up" title="Move up">
        ↑
      </button>
      <button className="btn small" type="button" disabled={busy || isLast} onClick={onMoveDown} aria-label="Move down" title="Move down">
        ↓
      </button>
      <button className="btn small danger" type="button" disabled={busy} onClick={onDelete} aria-label={`Delete ${column.name}`}>
        Delete
      </button>
    </li>
  );
}

/** Standalone board page (/boards/:id) — thin wrapper around <BoardCanvas>. */
export function BoardPage() {
  const { id = "" } = useParams();
  return (
    <>
      <PageBar
        crumbs={
          <>
            <Link to="/projects">Projects</Link> <span aria-hidden="true">/</span> Board
          </>
        }
      />
      <div className="content board-content">
        <BoardCanvas boardId={id} />
      </div>
    </>
  );
}

function BoardColumn({
  column,
  priorities,
  dragId,
  movingId,
  onDragState,
  onDropTask,
  onOpenTask,
}: {
  column: BoardColumnRow;
  priorities: PriorityIndex;
  dragId: string | null;
  movingId: string | null;
  onDragState: (id: string | null) => void;
  onDropTask: (taskId: string, columnId: string, afterTaskId: string | null) => void;
  onOpenTask: (id: string) => void;
}) {
  // Drop-target highlight. dragenter/dragleave also fire for every child node,
  // so track a depth counter and only clear when it returns to zero.
  const [dropOver, setDropOver] = useState(false);
  const depth = useRef(0);

  function handleDragEnter(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current += 1;
    setDropOver(true);
  }
  function handleDragLeave() {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDropOver(false);
  }
  function handleDragOver(e: DragEvent<HTMLElement>) {
    e.preventDefault(); // required for the column (and its cards) to accept drops
    e.dataTransfer.dropEffect = "move";
  }
  function handleDrop(e: DragEvent<HTMLElement>) {
    // A drop ONTO a card already called preventDefault (insert-after); only
    // treat untouched drops as "drop on the column background" = append.
    const handledByCard = e.defaultPrevented;
    e.preventDefault();
    depth.current = 0;
    setDropOver(false);
    if (handledByCard) return;
    const taskId = e.dataTransfer.getData("text/plain");
    if (!taskId) return;
    if (column.tasks.at(-1)?.id === taskId) return; // already last here — no-op
    // Append to the end: after the last sibling (excluding the dragged card);
    // an empty column passes null, which the API places at the top (only slot).
    const last = column.tasks.filter((t) => t.id !== taskId).at(-1) ?? null;
    onDropTask(taskId, column.id, last ? last.id : null);
  }

  const count = column.tasks.length;
  const limit = column.wip_limit ?? null;
  const overWip = limit != null && count > limit;
  // Derive a stable column accent from its name so each column reads distinctly.
  const accents = ["var(--st-open)", "var(--st-progress)", "var(--st-done)", "var(--accent)", "var(--brand-2)"];
  let h = 0;
  for (let i = 0; i < column.name.length; i++) h = (h * 31 + column.name.charCodeAt(i)) >>> 0;
  const colAccent = column.color || accents[h % accents.length];

  return (
    <section
      className={`board-col${dropOver ? " col-dropover" : ""}`}
      aria-label={column.name}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <header className="board-col-head">
        <span className="col-swatch" style={{ ["--col-accent" as string]: colAccent }} aria-hidden="true" />
        <span className="col-name">{column.name}</span>
        <span className="grow" />
        <span className={`count${overWip ? " warn" : ""}`}>
          {limit != null ? `${count} / ${limit}` : count}
        </span>
      </header>
      {overWip ? (
        <div className="board-col-wip">Over WIP limit — finish work before starting more</div>
      ) : null}
      <div className="board-col-body">
        {column.tasks.length === 0 ? (
          <p className="muted board-empty">No tasks</p>
        ) : (
          column.tasks.map((task) => (
            <BoardCard
              key={task.id}
              task={task}
              priorities={priorities}
              dragging={dragId === task.id}
              moving={movingId === task.id}
              onDragState={onDragState}
              onDropOnCard={(droppedId) => onDropTask(droppedId, column.id, task.id)}
              onOpen={() => onOpenTask(task.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function BoardCard({
  task,
  priorities,
  dragging,
  moving,
  onDragState,
  onDropOnCard,
  onOpen,
}: {
  task: TaskRow;
  priorities: PriorityIndex;
  /** True while THIS card is being dragged (ghost styling). */
  dragging: boolean;
  /** True while this card's move request is in flight (translucent). */
  moving: boolean;
  onDragState: (id: string | null) => void;
  /** Another card was dropped onto this one → insert it after this card. */
  onDropOnCard: (droppedTaskId: string) => void;
  onOpen: () => void;
}) {
  const priorityCode = taskPriorityCode(task, priorities);
  const due = dueInfo(task.due_date);

  return (
    <article
      className={`board-card${moving ? " busy" : ""}${dragging ? " card-dragging" : ""}`}
      style={{ ["--card-rail" as string]: railColor(task, priorityCode) }}
      draggable
      onDragStart={(e) => {
        // Interacting with the card's controls (move <select>, title button)
        // must not start a drag — otherwise the native select never opens.
        if ((e.target as HTMLElement).closest("select, button, input, a, option")) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData("text/plain", task.id);
        e.dataTransfer.effectAllowed = "move";
        onDragState(task.id);
      }}
      onDragEnd={() => onDragState(null)}
      onDrop={(e) => {
        // preventDefault (without stopPropagation) marks the drop as handled;
        // the column's onDrop still runs to clear its highlight but skips the
        // append path because e.defaultPrevented is set.
        e.preventDefault();
        const droppedId = e.dataTransfer.getData("text/plain");
        if (droppedId && droppedId !== task.id) onDropOnCard(droppedId);
      }}
    >
      {task.origin_ticket_id ? (
        <span className="board-card-origin">
          <svg className="icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="6" width="18" height="12" rx="2" />
            <path d="M3 10h18" />
          </svg>
          From ticket
        </span>
      ) : due ? (
        <span className={`board-card-date ${due.cls}`}>
          <CalendarGlyph /> {due.label}
        </span>
      ) : null}
      <button className="board-card-title" onClick={onOpen} title="Open project task">
        {task.title}
      </button>
      <div className="board-card-foot">
        {priorityCode ? <PriorityBadge code={priorityCode} /> : null}
        <span className="grow" />
        {task.story_points != null ? <span className="board-card-pts">{task.story_points} pt</span> : null}
        <Avatar name={task.assignee_name} size="sm" />
      </div>
    </article>
  );
}

function CreateBoardTaskModal({
  projectId,
  boardId,
  columns,
  priorities,
  onClose,
  onCreated,
}: {
  projectId: string;
  boardId: string;
  columns: BoardColumnRow[];
  priorities: PriorityIndex;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [columnId, setColumnId] = useState(columns[0]?.id ?? "");
  const [priorityId, setPriorityId] = useState("");
  const [estimate, setEstimate] = useState("");
  const [department, setDepartment] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const { run, pending, error } = useMutation(createTask);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const est = estimate.trim();
    const res = await run({
      project_id: projectId,
      title: title.trim(),
      board_id: boardId,
      ...(columnId ? { column_id: columnId } : {}),
      ...(priorityId ? { priority_id: priorityId } : {}),
      ...(est !== "" ? { estimate_minutes: Math.max(0, Math.round(Number(est))) } : {}),
      ...(department.trim() ? { department: department.trim() } : {}),
      ...(tags.length ? { tags } : {}),
    });
    if (res) onCreated();
  }

  return (
    <Modal
      title="New project task"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-board-task-form"
            disabled={pending || !title.trim()}
          >
            {pending ? <Spinner label="Creating…" /> : "Create task"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-board-task-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs doing?"
            autoFocus
            required
          />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Column</span>
            <select value={columnId} onChange={(e) => setColumnId(e.target.value)}>
              {columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Priority</span>
            <select
              value={priorityId}
              onChange={(e) => setPriorityId(e.target.value)}
              disabled={priorities.loading}
            >
              <option value="">—</option>
              {priorities.list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="row">
          <label className="field">
            <span className="label">Estimate (min)</span>
            <input
              type="number"
              min={0}
              step={5}
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              placeholder="e.g. 150"
            />
          </label>
          <label className="field">
            <span className="label">Department</span>
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="Owning office / department"
            />
          </label>
        </div>
        <div className="field">
          <span className="label">Tags</span>
          <TagsEditor value={tags} onChange={setTags} inputId="create-board-task-tags" />
        </div>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Sprints — collapsible section under the live board. Lists the board's
// sprints, creates/starts/completes/deletes them, and for the ACTIVE sprint
// shows a burndown mini-chart plus a velocity strip (closed sprints).
// ===========================================================================

/** State → chip class for the sprint state pill. */
function sprintStateTag(state: string): string {
  switch (state) {
    case "active":
      return "tag tag-ok";
    case "closed":
      return "tag tag-neutral";
    default:
      return "tag tag-info"; // future
  }
}

/** Parse a yyyy-mm-dd input into unix seconds (local midnight); null when empty. */
function dateInputToUnix(value: string): number | null {
  if (!value) return null;
  const ms = new Date(`${value}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

function SprintsSection({ boardId, tasks }: { boardId: string; tasks: TaskRow[] }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="card" style={{ marginTop: 18 }} aria-label="Sprints">
      <header className="card-head">
        <h2>Sprints</h2>
        <button className="btn small" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? "Hide" : "Show"}
        </button>
      </header>
      {open ? (
        <div className="card-body">
          <SprintsBody boardId={boardId} tasks={tasks} />
        </div>
      ) : null}
    </section>
  );
}

function SprintsBody({ boardId, tasks }: { boardId: string; tasks: TaskRow[] }) {
  const { data, loading, error, reload } = useApi((signal) => listSprints(boardId, signal), [boardId]);
  const startMut = useMutation(startSprint);
  const completeMut = useMutation(completeSprint);
  const deleteMut = useMutation(deleteSprint);
  const [notice, setNotice] = useState<string | null>(null);

  if (loading) return <Loading label="Loading sprints…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const sprints = data?.sprints ?? [];
  const active = sprints.find((s) => s.state === "active") ?? null;
  const mutationError = startMut.error ?? completeMut.error ?? deleteMut.error;

  async function onStart(sprint: SprintRow) {
    setNotice(null);
    const res = await startMut.run(sprint.id);
    if (res) reload();
  }

  async function onComplete(sprint: SprintRow) {
    setNotice(null);
    const res = await completeMut.run(sprint.id);
    if (res) {
      setNotice(
        `Sprint "${sprint.name}" completed — ${res.moved} unfinished task${res.moved === 1 ? "" : "s"} returned to the backlog.`,
      );
      reload();
    }
  }

  async function onDelete(sprint: SprintRow) {
    if (!(await confirmDialog({ message: `Delete sprint "${sprint.name}"? Its tasks return to the backlog.`, danger: true }))) return;
    setNotice(null);
    const res = await deleteMut.run(sprint.id);
    if (res) reload();
  }

  const busy = startMut.pending || completeMut.pending || deleteMut.pending;

  return (
    <>
      {notice ? <Alert kind="success">{notice}</Alert> : null}
      {mutationError ? <Alert kind="error">{mutationError}</Alert> : null}

      {sprints.length === 0 ? (
        <EmptyState>No sprints yet — create the first one below.</EmptyState>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Sprint</th>
              <th>State</th>
              <th>Start</th>
              <th>End</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {sprints.map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{s.name}</strong>
                  {s.goal ? <div className="sub">{s.goal}</div> : null}
                </td>
                <td>
                  <span className={sprintStateTag(s.state)}>{s.state}</span>
                </td>
                <td className="num">{formatDateShort(s.start_at)}</td>
                <td className="num">{formatDateShort(s.end_at)}</td>
                <td className="row-actions">
                  {s.state === "future" ? (
                    <button className="btn small primary" disabled={busy} onClick={() => onStart(s)}>
                      Start
                    </button>
                  ) : null}
                  {s.state === "active" ? (
                    <button className="btn small" disabled={busy} onClick={() => onComplete(s)}>
                      Complete
                    </button>
                  ) : null}
                  <button className="btn small danger" disabled={busy} onClick={() => onDelete(s)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <CreateSprintForm boardId={boardId} onCreated={reload} />

      {active ? <ActiveSprintPanel sprint={active} tasks={tasks} /> : null}
      <VelocityStrip boardId={boardId} closedCount={sprints.filter((s) => s.state === "closed").length} />
    </>
  );
}

function CreateSprintForm({ boardId, onCreated }: { boardId: string; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [endDate, setEndDate] = useState("");
  const { run, pending, error } = useMutation(createSprint);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const endAt = dateInputToUnix(endDate);
    const res = await run(boardId, {
      name: name.trim(),
      ...(goal.trim() ? { goal: goal.trim() } : {}),
      ...(endAt != null ? { end_at: endAt } : {}),
    });
    if (res) {
      setName("");
      setGoal("");
      setEndDate("");
      onCreated();
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ marginTop: 14 }}>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <div className="row">
        <label className="field">
          <span className="label">New sprint</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sprint 7"
            required
          />
        </label>
        <label className="field">
          <span className="label">Goal (optional)</span>
          <input
            type="text"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="What this sprint should achieve"
          />
        </label>
        <label className="field">
          <span className="label">End date (optional)</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>
      <button className="btn primary small" type="submit" disabled={pending || !name.trim()}>
        {pending ? <Spinner label="Creating…" /> : "Create sprint"}
      </button>
    </form>
  );
}

/** Burndown for the active sprint + a picker to pull board tasks into it. */
function ActiveSprintPanel({ sprint, tasks }: { sprint: SprintRow; tasks: TaskRow[] }) {
  const { data, loading, error, reload } = useApi((signal) => getBurndown(sprint.id, signal), [sprint.id]);
  const addMut = useMutation(addTaskToSprint);
  const [taskId, setTaskId] = useState("");

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    if (!taskId) return;
    const res = await addMut.run(sprint.id, taskId);
    if (res) {
      setTaskId("");
      reload();
    }
  }

  return (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ margin: "0 0 8px" }}>
        Active sprint — {sprint.name}{" "}
        {sprint.end_at ? <span className="faint">(ends {formatDateShort(sprint.end_at)})</span> : null}
      </h3>

      <form onSubmit={onAdd} className="row sprint-add-row" style={{ alignItems: "flex-end", marginBottom: 12 }}>
        <label className="field">
          <span className="label">Add a board task to this sprint</span>
          <select value={taskId} onChange={(e) => setTaskId(e.target.value)} disabled={addMut.pending}>
            <option value="">— choose a task —</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
                {t.story_points != null ? ` (${t.story_points} pt)` : ""}
              </option>
            ))}
          </select>
        </label>
        <button className="btn small" type="submit" disabled={addMut.pending || !taskId}>
          {addMut.pending ? <Spinner label="Adding…" /> : "Add to sprint"}
        </button>
      </form>
      {addMut.error ? <Alert kind="error">{addMut.error}</Alert> : null}

      {loading ? (
        <Loading label="Loading burndown…" />
      ) : error ? (
        // The API 400s until the sprint has both start and end dates.
        <Alert kind="info">Burndown unavailable: {error}</Alert>
      ) : data && data.days.length > 0 ? (
        <>
          <div className="sub" style={{ marginBottom: 8 }}>
            Burndown — remaining story points per day vs ideal ({data.total_points} pt committed)
          </div>
          <div className="bars">
            {data.days.map((d) => {
              const max = Math.max(1, data.total_points);
              const behind = d.remaining > d.ideal;
              return (
                <div className="bar-row" key={d.date} title={`Ideal ${d.ideal} pt · remaining ${d.remaining} pt`}>
                  <div className="bar-label">{d.date.slice(5)}</div>
                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${Math.min(100, (d.remaining / max) * 100)}%`,
                        ...(behind ? { background: "var(--st-blocked)" } : {}),
                      }}
                    />
                  </div>
                  <div className="bar-value num">{d.remaining}</div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <EmptyState>No burndown data yet.</EmptyState>
      )}
    </div>
  );
}

/** Velocity strip: committed vs completed points for each CLOSED sprint. */
function VelocityStrip({ boardId, closedCount }: { boardId: string; closedCount: number }) {
  const { data, loading, error, reload } = useApi((signal) => getVelocity(boardId, signal), [boardId, closedCount]);

  if (loading) return <Loading label="Loading velocity…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  const rows = data?.velocity ?? [];
  if (rows.length === 0) return null; // No closed sprints yet — nothing to chart.

  const max = Math.max(1, ...rows.map((r) => Math.max(r.committed_points, r.completed_points)));
  return (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ margin: "0 0 8px" }}>Velocity</h3>
      <div className="sub" style={{ marginBottom: 8 }}>
        Completed vs committed story points per closed sprint
      </div>
      <div className="bars">
        {rows.map((r) => (
          <div
            className="bar-row"
            key={r.sprint_id}
            title={`Committed ${r.committed_points} pt · completed ${r.completed_points} pt`}
          >
            <div className="bar-label">{r.name}</div>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${(r.completed_points / max) * 100}%` }} />
            </div>
            <div className="bar-value num">
              {r.completed_points}/{r.committed_points}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
